// Popup preview of a Comms Brief / Work Order: draws the same flat block list (title, headings, labelled
// values, bullets, tables) the Word and PDF exports are built from, as returned by
// POST /program-documents/{id}/preview. Looks like a page; nothing here is editable and nothing is saved.
//
// Props: blocks [{t, ...}] | null (while loading), error?: string, onClose: () => void, footnote?: string
import { useEffect } from 'react';

const PAGE = { fontFamily: "Calibri, 'Helvetica Neue', Arial, sans-serif", color: '#1b1f2a', fontSize: '14px', lineHeight: 1.5 };

export function PreviewBlocks({ blocks }) {
  return (
    <div style={PAGE}>
      {blocks.map((b, i) => {
        if (b.t === 'title') return <h1 key={i} style={{ fontSize: '22px', margin: '0 0 14px', color: '#131843' }}>{b.text}</h1>;
        if (b.t === 'h1') return <h2 key={i} style={{ fontSize: '17px', margin: '20px 0 6px', color: '#131843', borderBottom: '1px solid #e3e7ee', paddingBottom: '3px' }}>{b.text}</h2>;
        if (b.t === 'h2') return <h3 key={i} style={{ fontSize: '15px', margin: '14px 0 4px', color: '#2c3e50' }}>{b.text}</h3>;
        if (b.t === 'kv') {
          return (
            <p key={i} style={{ margin: '0 0 6px', whiteSpace: 'pre-wrap' }}>
              <strong>{b.label}: </strong>{b.text}
            </p>
          );
        }
        if (b.t === 'bullets') {
          return (
            <div key={i} style={{ margin: '0 0 6px' }}>
              <strong>{b.label}:</strong>
              <ul style={{ margin: '2px 0 0', paddingLeft: '22px' }}>{b.items.map((it, j) => <li key={j}>{it}</li>)}</ul>
            </div>
          );
        }
        if (b.t === 'table') {
          return (
            <div key={i} style={{ margin: '0 0 8px', overflowX: 'auto' }}>
              <strong>{b.label}:</strong>
              <table style={{ borderCollapse: 'collapse', width: '100%', marginTop: '3px' }}>
                <thead>
                  <tr>{b.columns.map(c => <th key={c.key} style={{ border: '1px solid #cfd6df', background: '#f5f6f9', padding: '4px 8px', textAlign: 'left', fontSize: '13px' }}>{c.label}</th>)}</tr>
                </thead>
                <tbody>
                  {b.rows.map((r, j) => (
                    <tr key={j}>{b.columns.map(c => <td key={c.key} style={{ border: '1px solid #cfd6df', padding: '4px 8px', verticalAlign: 'top', fontSize: '13px' }}>{r[c.key]}</td>)}</tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        return null;
      })}
    </div>
  );
}

export default function DocumentPreview({ blocks, error, onClose, footnote }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div role="dialog" aria-modal="true" aria-label="Document preview" onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(19,24,67,0.55)', zIndex: 1500, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '28px 16px', overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: '#fff', width: '100%', maxWidth: '860px', borderRadius: '8px', boxShadow: '0 10px 40px rgba(0,0,0,0.35)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 18px', borderBottom: '1px solid #e3e7ee', background: '#f8f9fa', borderRadius: '8px 8px 0 0' }}>
          <strong style={{ color: '#2c3e50', fontSize: '14px' }}>Preview</strong>
          <button type="button" onClick={onClose}
            style={{ padding: '5px 14px', background: '#2c3e50', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>Close</button>
        </div>
        <div style={{ padding: '28px 44px 32px' }}>
          {error && <div style={{ color: '#922b21' }}>{error}</div>}
          {!error && !blocks && <div style={{ color: '#757575' }}>Building the preview…</div>}
          {!error && blocks && (blocks.length <= 1
            ? <><PreviewBlocks blocks={blocks} /><div style={{ color: '#757575', marginTop: '10px' }}>Nothing has been filled in yet.</div></>
            : <PreviewBlocks blocks={blocks} />)}
        </div>
        {footnote && <div style={{ padding: '8px 18px', borderTop: '1px solid #e3e7ee', fontSize: '12px', color: '#757575' }}>{footnote}</div>}
      </div>
    </div>
  );
}
